/**
 * SavedViewsBar — admin's saved smart-queue filter views (iter114s.76).
 *
 * Renders below the chip row on the Action Queue. Each view is a
 * stored (name, queue) pair the admin saves so frequently-used filters
 * (e.g. "My morning queue" = `rush`) are one click away.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bookmark, BookmarkPlus, X, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SavedViewsBar = ({ currentQueue, onPickQueue }) => {
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [naming, setNaming] = useState(false);
  const [newName, setNewName] = useState('');

  const fetchViews = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/saved-queue-views`, { withCredentials: true });
      setViews(r.data?.views || []);
    } catch { /* non-fatal */ }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchViews(); }, []);

  const handleSave = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await axios.post(`${API_URL}/api/admin/saved-queue-views`,
        { name, queue: currentQueue }, { withCredentials: true });
      toast.success(`Saved "${name}".`);
      setNewName(''); setNaming(false); fetchViews();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed.'); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/admin/saved-queue-views/${id}`, { withCredentials: true });
      fetchViews();
    } catch (e) { toast.error('Delete failed.'); }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="saved-views-bar">
      <span className="text-[10px] font-bold uppercase tracking-wider text-[#1F3D2C]/55 flex items-center gap-1">
        <Bookmark className="w-3 h-3" /> Saved
      </span>
      {loading ? (
        <Loader2 className="w-3 h-3 text-[#6FBF73] animate-spin" />
      ) : (
        <>
          {views.map((v) => (
            <div key={v.id} className="inline-flex items-center bg-[#E8F1E6] rounded-full overflow-hidden border border-[#6FBF73]/30">
              <button
                type="button"
                onClick={() => onPickQueue(v.queue)}
                data-testid={`saved-view-${v.id}`}
                className={`text-[11px] font-semibold pl-2.5 pr-1.5 py-1 transition ${
                  currentQueue === v.queue ? 'text-[#14281D]' : 'text-[#2F5D3A] hover:text-[#14281D]'
                }`}
              >
                {v.name} <span className="opacity-50">· {v.queue}</span>
              </button>
              <button
                type="button"
                onClick={() => handleDelete(v.id)}
                aria-label={`Delete saved view ${v.name}`}
                className="pl-1 pr-2 py-1 text-[#1F3D2C]/40 hover:text-red-500 transition"
                data-testid={`saved-view-delete-${v.id}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          {naming ? (
            <div className="inline-flex items-center gap-1">
              <input
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSave();
                  if (e.key === 'Escape') { setNaming(false); setNewName(''); }
                }}
                placeholder={`Name for "${currentQueue}"`}
                className="text-[11px] border border-[#6FBF73]/40 rounded px-2 py-1 w-44"
                data-testid="saved-view-name-input"
              />
              <button
                type="button"
                onClick={handleSave}
                className="text-[11px] font-semibold bg-[#6FBF73] text-white rounded-full px-2.5 py-1"
                data-testid="saved-view-save"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => { setNaming(false); setNewName(''); }}
                className="text-[11px] text-gray-500"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => { setNaming(true); setNewName(currentQueue); }}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#2F5D3A] hover:text-[#14281D] bg-white hover:bg-[#F4FBF6] border border-[#E5E7EB] rounded-full px-2.5 py-1 transition"
              data-testid="saved-view-add"
            >
              <BookmarkPlus className="w-3 h-3" />
              Save current
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default SavedViewsBar;
